import React, { useState, useCallback } from 'react';
import FileUpload from './FileUpload';
import FileList from './FileList';
import ProgressBar from './ProgressBar';
import { resumeDocument } from '../services/pdfService';
import { CopyIcon, ResumeIcon, SpinnerIcon, ErrorIcon, CheckIcon } from './icons';
import { useTranslation } from '../i18n';

const ResumeTool: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [language, setLanguage] = useState('English');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ filename: string; summary: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleFileSelect = useCallback((selectedFiles: File[]) => {
    if (selectedFiles.length > 0) {
      setFile(selectedFiles[0]);
      setError(null);
      setResult(null);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  const handleResume = async () => {
    if (!file) {
      setError(t('errorSelectFileToSummarize'));
      return;
    }
    setIsLoading(true);
    setIsProcessing(false);
    setProgress(0);
    setError(null);
    setResult(null);
    try {
      const data = await resumeDocument(file, language, setProgress);
      setIsProcessing(true);
      setResult(data);
    } catch (err: any) {
      setError(err.message || t('errorUnexpected'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result.summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-800/50 rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-700">
      <FileUpload onFilesSelect={handleFileSelect} multiple={false} accept=".pdf" />
      
      {file && <FileList files={[file]} onRemoveFile={handleRemoveFile} />}
      
      {file && (
        <div className="mt-6">
          <label htmlFor="language-select" className="block text-sm font-medium text-slate-300 mb-2">
            {t('summaryLanguageLabel')}
          </label>
          <select
            id="language-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg py-2.5 px-3 text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
          >
            <option value="English">{t('langEnglish')}</option>
            <option value="Portuguese">{t('langPortuguese')}</option>
            <option value="Spanish">{t('langSpanish')}</option>
          </select>
        </div>
      )}

      {isLoading && <ProgressBar progress={progress} isProcessing={isProcessing} />}

      <div className="mt-6">
        <button
          onClick={handleResume}
          disabled={isLoading || !file}
          className="w-full flex items-center justify-center gap-3 bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-all duration-200"
        >
          {isLoading ? <SpinnerIcon /> : <ResumeIcon />}
          <span>{isLoading ? (isProcessing ? t('statusGenerating') : t('statusUploading')) : t('resumeButton')}</span>
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg flex items-center gap-3">
            <ErrorIcon/>
            <span>{error}</span>
        </div>
      )}
      {result && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-lg text-slate-200">{t('summaryLabel')}</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold py-2 px-3 rounded-md transition-colors"
              >
                {copied ? <CheckIcon/> : <CopyIcon/>}
                {copied ? t('copiedButton') : t('copyButton')}
              </button>
            </div>
          </div>
          <div className="w-full p-4 bg-slate-900/70 border border-slate-700 rounded-lg text-slate-300 text-sm">
            <p className="font-medium text-slate-400 mb-2">{t('originalFileLabel')} {result.filename}</p>
            <p className="whitespace-pre-wrap leading-relaxed">{result.summary}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeTool;
